import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { CardRow } from '../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../generic/ui/Mono'
import SproutIcon from '../../../../../generic/icons/IconLib/SproutIcon'
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon'
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon'
import HashIcon from '../../../../../generic/icons/IconLib/HashIcon'
import IconTextField from '../../../../../generic/ui/Input/IconTextField'
import { IPalletDetailsForDivide } from '../types/palletDetailsForDivide'
import { EXIST_PATH } from '../../../../../modules/API/settings'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'

type Props = IPalletDetailsForDivide & {
    handleChangeQuantityForDivide: (recID: number, quantity: number) => void
}

const DEBOUNCE_MS = 1000

const PalletDivideItemCard: React.FC<Props> = ({
    RecID,
    GeneralProductID,
    ProductDesc,
    VarietyDesc,
    SizeID,
    Pack_Name,
    Quantity,
    WeightNeto,
    QuantityForDivide,
    handleChangeQuantityForDivide,
}) => {
    const [t] = useTranslation('controllPallet')
    const [qtyToDivide, setQtyToDivide] = useState<number>(QuantityForDivide)

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (qtyToDivide !== QuantityForDivide) {
                handleChangeQuantityForDivide(RecID, qtyToDivide)
            }
        }, DEBOUNCE_MS)

        return () => clearTimeout(timeoutId)
    }, [qtyToDivide, RecID, handleChangeQuantityForDivide, QuantityForDivide])

    const handleChangeQty = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = Number(e.target.value)
        if (next > Quantity) {
            handleNotifications(t('controllPallet_dividePallet_notification_overflow'), {
                type: 'warning',
            })
            return
        }
        setQtyToDivide(next)
    }

    const product = `${ProductDesc?.trim() ?? ''} ${VarietyDesc?.trim() ?? ''} ${SizeID?.trim() ?? ''}`.trim()
    const productImg = GeneralProductID
        ? `${EXIST_PATH}/images/products/${GeneralProductID}.png`
        : '/emptyBox.png'

    return (
        <Wrapper>
            <CardRow
                $columns='2.75rem minmax(0, 1fr) auto 9rem'
                $mobileColumns='2.5rem minmax(0, 1fr) 7rem'
                $isLast
                $hover={false}
            >
                <CardRow.Cell $align='center'>
                    <ProductImg
                        src={productImg}
                        alt={product}
                        loading='lazy'
                        decoding='async'
                        onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).src = '/emptyBox.png'
                        }}
                    />
                </CardRow.Cell>

                <CardRow.Cell>
                    <CardRow.Stack $gap='0.25rem'>
                        <CardRow.Line $gap='0.5rem'>
                            <SmallIcon $muted>
                                <SproutIcon />
                            </SmallIcon>
                            <PrimaryName>{product}</PrimaryName>
                        </CardRow.Line>
                        <Metas>
                            {Pack_Name && (
                                <MetaLine>
                                    <MetaIcon>
                                        <PackageBoxIcon />
                                    </MetaIcon>
                                    <MetaText>
                                        {Pack_Name.trim()}
                                        {' · '}
                                        <Mono>{Quantity}</Mono>
                                    </MetaText>
                                </MetaLine>
                            )}
                            {typeof WeightNeto === 'number' && (
                                <MetaLine>
                                    <MetaIcon>
                                        <ScaleIcon />
                                    </MetaIcon>
                                    <MetaText>
                                        <Mono>{WeightNeto.toFixed(1)}</Mono>
                                    </MetaText>
                                </MetaLine>
                            )}
                        </Metas>
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='end'>
                    <FieldLabel>כמות לפיצול</FieldLabel>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <IconTextField
                        type='number'
                        leadingIcon={<HashIcon />}
                        value={qtyToDivide}
                        onChange={handleChangeQty}
                        onFocus={(e) => e.currentTarget.select()}
                    />
                </CardRow.Cell>
            </CardRow>
        </Wrapper>
    )
}

export default PalletDivideItemCard

const Wrapper = styled.div`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`

const ProductImg = styled.img`
    width: 2.5rem;
    height: 2.5rem;
    object-fit: contain;
    border-radius: 0.5rem;
    user-select: none;
    pointer-events: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.25rem;
        height: 2.25rem;
    }
`

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;

    svg {
        width: 1rem;
        height: 1rem;
    }
`

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`

const Metas = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
`

const MetaLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`

const MetaIcon = styled.span`
    color: ${({ theme }) => theme.colors.ink3};
    display: inline-flex;
    align-items: center;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`

const MetaText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const FieldLabel = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    padding-inline-end: 0.5rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: none;
    }
`
